'use client';

import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

import { Trans } from '@kit/ui/trans';

const HERO_VIDEO_SRC = '/agency/videos/hero.mp4';

export function HeroSection() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const videoY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  return (
    <section
      ref={ref}
      className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-black px-8 md:px-16"
      data-testid="hero-section"
    >
      <motion.div style={{ y: videoY }} className="absolute inset-0 scale-110">
        <video
          src={HERO_VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          className="h-full w-full object-cover"
        />
      </motion.div>

      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.65) 100%)',
        }}
      />

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 mx-auto max-w-4xl text-center"
      >
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8 leading-none text-white md:mb-10"
          style={{
            fontFamily: 'var(--font-bebas-neue), Impact, sans-serif',
            fontWeight: 300,
            fontSize: 'clamp(3.5rem, 9vw, 8rem)',
            letterSpacing: '0.06em',
          }}
          data-testid="hero-headline"
        >
          {/* i18n-ignore brand name */}
          Ballee Agency
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 leading-snug text-white/80 md:mb-20"
          style={{
            fontFamily: 'var(--font-bebas-neue), Impact, sans-serif',
            fontWeight: 300,
            fontSize: 'clamp(1.1rem, 2.8vw, 1.9rem)',
            letterSpacing: '0.02em',
          }}
          data-testid="hero-tagline"
        >
          <Trans
            i18nKey="agency:hero.tagline"
            components={{
              em: (
                <em
                  style={{
                    fontFamily:
                      'var(--font-inter-agency), -apple-system, sans-serif',
                    fontStyle: 'italic',
                    fontWeight: 300,
                    letterSpacing: '0.04em',
                  }}
                />
              ),
            }}
          />
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <a
            href="#contact"
            className="inline-block border-b border-white/50 pb-1 text-xs tracking-[0.25em] text-white uppercase transition-colors duration-500 hover:border-white"
            style={{
              fontFamily: 'var(--font-inter-agency), -apple-system, sans-serif',
              fontWeight: 400,
            }}
            data-testid="hero-cta"
          >
            <Trans i18nKey="agency:hero.cta" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2, delay: 2 }}
        className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          className="h-12 w-px bg-white/30"
        />
      </motion.div>
    </section>
  );
}
